<template>
  <!-- WI 1951: ren navigasjon rendres som ekte lenke (hover viser URL, midtklikk/ny
       fane virker). `to` velger lenke-varianten; uten `to` er det en vanlig knapp. -->
  <RouterLink
    v-if="to && !disabled"
    :to="to"
    :class="[sharedClasses, 'cursor-pointer no-underline', typeClasses]"
    @click="handleClick"
  >
    <font-awesome-icon
      v-if="loading"
      :icon="['fal', 'spinner']"
      size="lg"
      spin
    />
    <font-awesome-icon
      v-else-if="icon && iconStyle"
      :icon="[iconStyle, icon]"
      size="lg"
    />
    <slot />
  </RouterLink>
  <button
    v-else
    :class="[
      sharedClasses,
      disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer',
      typeClasses,
    ]"
    :type="nativeType"
    :disabled="disabled"
    @click="handleClick"
  >
    <font-awesome-icon
      v-if="loading"
      :icon="['fal', 'spinner']"
      size="lg"
      spin
    />
    <font-awesome-icon
      v-else-if="icon && iconStyle"
      :icon="[iconStyle, icon]"
      size="lg"
    />
    <slot />
  </button>
</template>

<script setup>
import { computed } from 'vue';
import { RouterLink } from 'vue-router';

const props = defineProps({
  icon: {
    type: String,
    required: false,
    validator: (value) => !value || value.trim().length > 0,
    default: null
  },
  iconStyle: {
    type: String,
    default: 'fal'
  },
  type: {
    type: String,
    default: 'default'
  },
  // Native HTML button type for the rendered <button>. The `type` prop above is the visual
  // variant (primary/default/cancel/...), so it cannot double as the DOM type attribute. Default
  // 'button' stops non-form buttons from implicitly submitting a surrounding form (S9011). A form
  // submit button passes native-type="submit".
  nativeType: {
    type: String,
    default: 'button'
  },
  loading: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  flat: {
    type: Boolean,
    default: true
  },
  // WI 1951: route-mål (samme form som router-link `to`). Satt → ekte lenke.
  to: {
    type: [String, Object],
    default: null
  }
});

const sharedClasses = 'inline-flex items-center gap-3 px-3 py-1.5 rounded-md border-solid appearance-none font-medium text-sm leading-snug transition-colors focus:outline-none';

const emit = defineEmits(['click']);

// Solid (flat=false) variant classes keyed by button type. off = disabled, on = enabled.
// Unknown types resolve to SOLID_TYPE_FALLBACK, mirroring the original switch default branch.
const SOLID_TYPE_CLASSES = new Map([
  ['primary', {
    off: 'border border-border bg-muted text-fg-muted',
    on: 'border border-accent bg-accent text-fg-on-accent hover:bg-accent-emphasis hover:border-accent-emphasis'
  }],
  ['default', {
    off: 'border border-border bg-muted text-fg-muted',
    on: 'border border-accent bg-accent text-fg-on-accent hover:bg-accent-emphasis hover:border-accent-emphasis'
  }],
  ['cancel', {
    off: 'border border-border bg-muted text-fg-muted',
    on: 'border border-border bg-muted text-fg hover:bg-muted'
  }],
  ['delete', {
    off: 'border border-border bg-muted text-fg-muted',
    on: 'border border-danger bg-danger text-fg-on-accent hover:bg-danger/90'
  }],
  ['danger', {
    off: 'border border-border bg-muted text-fg-muted',
    on: 'border border-danger bg-danger text-fg-on-accent hover:bg-danger/90'
  }],
  ['info', {
    off: 'border border-border bg-muted text-fg-muted',
    on: 'border border-info bg-info text-fg-on-accent hover:bg-info/90'
  }],
  ['warning', {
    off: 'border border-border bg-muted text-fg-muted',
    on: 'border border-warning bg-warning text-fg hover:bg-warning/90'
  }]
]);
const SOLID_TYPE_FALLBACK = {
  off: 'border border-border bg-muted text-fg-muted',
  on: 'border border-border bg-surface text-fg hover:bg-muted'
};

// Flat (default) variant classes keyed by button type. off = disabled, on = enabled.
// Unknown types resolve to FLAT_TYPE_FALLBACK, mirroring the original switch default branch.
// WI 2124: the on-accent variant is for buttons on dark gradient/accent headers. Light text
// stays visible at rest (not accent-blue which vanishes on the background), with a subtle
// hover/focus. It mirrors BaseIconButton on-accent.
const FLAT_TYPE_CLASSES = new Map([
  ['primary', {
    off: 'text-accent',
    on: 'text-accent hover:bg-accent-subtle focus-visible:bg-accent-subtle'
  }],
  ['default', {
    off: 'text-accent',
    on: 'text-accent hover:bg-accent-subtle focus-visible:bg-accent-subtle'
  }],
  ['on-accent', {
    off: 'text-fg-on-accent/60',
    on: 'text-fg-on-accent hover:bg-fg-on-accent/20 focus-visible:bg-fg-on-accent/20'
  }],
  ['cancel', {
    off: 'text-fg-muted',
    on: 'text-fg-muted hover:bg-muted hover:text-fg focus-visible:bg-muted focus-visible:text-fg'
  }],
  ['delete', {
    off: 'text-danger',
    on: 'text-danger hover:bg-danger-subtle focus-visible:bg-danger-subtle'
  }],
  ['danger', {
    off: 'text-danger',
    on: 'text-danger hover:bg-danger-subtle focus-visible:bg-danger-subtle'
  }],
  ['info', {
    off: 'text-info',
    on: 'text-info hover:bg-info-subtle focus-visible:bg-info-subtle'
  }],
  ['warning', {
    off: 'text-warning',
    on: 'text-warning hover:bg-warning-subtle focus-visible:bg-warning-subtle'
  }]
]);
const FLAT_TYPE_FALLBACK = {
  off: 'text-fg',
  on: 'text-fg hover:bg-muted focus-visible:bg-muted'
};

const typeClasses = computed(() => {
  const table = props.flat ? FLAT_TYPE_CLASSES : SOLID_TYPE_CLASSES;
  const fallback = props.flat ? FLAT_TYPE_FALLBACK : SOLID_TYPE_FALLBACK;
  const entry = table.get(props.type) ?? fallback;
  return props.disabled ? entry.off : entry.on;
});

function handleClick (event) {
  if (props.disabled) return;
  emit('click', event);
}
</script>
